import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthHero from '../../../shared/auth/components/AuthHero';
import { useFlowProtection } from '../../../shared/hooks/useFlowProtection';

const CompanyAdminRecruiterSetup: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Flow protection - only accessible after company signup flow
    const { isValidAccess } = useFlowProtection({
        storageKey: 'fromCompanySignup',
        fallbackPath: '/auth/company/signup'
    });

    // Get user data from location state or localStorage
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '********',
        jobTitle: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [warningMessage, setWarningMessage] = useState('');
    const [_userName, setUserName] = useState('');

    useEffect(() => {
        // Get user data from localStorage or location state
        const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        const stateData = location.state;

        setFormData({
            firstName: stateData?.firstName || authUser.first_name || '',
            lastName: stateData?.lastName || authUser.last_name || '',
            email: stateData?.email || authUser.email || '',
            password: '********',
            jobTitle: '',
        });

        setUserName(stateData?.firstName || authUser.first_name || 'there');
    }, [location.state]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
        setError('');
    };

    const handleDisabledFieldClick = () => {
        setWarningMessage('This is your recruiter account. We recommend using the same credentials.');
        setTimeout(() => setWarningMessage(''), 4000);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.jobTitle) {
            setError('Job Title is required');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('authToken');

            // Get company ID from companyData
            let companyId: string | null = null;
            const companyDataStr = localStorage.getItem('companyData');
            if (companyDataStr) {
                try {
                    const companyData = JSON.parse(companyDataStr);
                    companyId = companyData.id;
                } catch {
                    // Ignore parse errors
                }
            }

            if (!companyId) {
                setError('Company ID not found. Please complete company profile first.');
                setIsLoading(false);
                return;
            }

            if (!token) {
                setError('Authentication token not found. Please log in again.');
                setIsLoading(false);
                return;
            }

            // Call backend to create recruiter profile in MongoDB
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';
            const response = await fetch(`${apiBaseUrl}/auth/v1/recruiter/profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    job_title: formData.jobTitle,
                    location: 'Not specified',
                    company_id: companyId
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || 'Failed to create recruiter profile');
            }

            const result = await response.json();
            console.log('✅ Recruiter profile created:', result);

            // Also store in localStorage for quick access
            const recruiterProfile = {
                recruiterId: result.payload?.recruiter_id || result.recruiter_id,
                jobTitle: formData.jobTitle,
                companyId: companyId,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                createdAt: new Date().toISOString()
            };
            localStorage.setItem('recruiterProfile', JSON.stringify(recruiterProfile));

            // Navigate to admin welcome page
            navigate('/auth/company/admin-welcome', {
                state: {
                    firstName: formData.firstName,
                    companyName: location.state?.companyName || 'your company'
                }
            });

        } catch (err: any) {
            console.error('❌ Failed to create recruiter profile:', err);
            setError(err.message || 'Failed to create recruiter profile');
        } finally {
            setIsLoading(false);
        }
    };

    // Show nothing while checking access (redirect happens in hook if invalid)
    if (isValidAccess === null || isValidAccess === false) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#F8F9FB] flex">
            {/* Left Side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-[597px]">
                    {/* Logo */}
                    <div className="mb-8">
                        <img
                            src="/branding/logo_blue.svg"
                            alt="Hush"
                            className="h-12 w-auto"
                        />
                    </div>

                    {/* Title */}
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '48px',
                            lineHeight: '100%',
                            color: '#015FA6',
                            marginBottom: '24px'
                        }}
                    >
                        Create Your Recruiter Profile
                    </h1>

                    {/* Subtitle */}
                    <p
                        style={{
                            fontFamily: 'SF Pro',
                            fontWeight: 110,
                            fontSize: '18px',
                            lineHeight: '150%',
                            color: '#015FA6',
                            marginBottom: '48px'
                        }}
                    >
                        This will be your personal workspace inside Hush.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* First Name and Last Name - Two Columns */}
                        <div style={{ display: 'flex', gap: '22px', marginBottom: '24px' }}>
                            {/* First Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    disabled
                                    onClick={handleDisabledFieldClick}
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>

                            {/* Last Name */}
                            <div style={{ flex: 1 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        fontFamily: 'Manrope',
                                        fontWeight: 500,
                                        fontSize: '16px',
                                        lineHeight: '20px',
                                        letterSpacing: '-0.154px',
                                        color: '#015FA6',
                                        marginBottom: '14px'
                                    }}
                                >
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    disabled
                                    onClick={handleDisabledFieldClick}
                                    style={{
                                        width: '100%',
                                        height: '55px',
                                        padding: '10px 12px',
                                        fontFamily: 'Manrope',
                                        fontSize: '16px',
                                        color: '#015FA6',
                                        background: '#F7FAFC',
                                        border: '1px solid #CBD5E0',
                                        borderRadius: '8px',
                                        outline: 'none',
                                        boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                        letterSpacing: '-0.154px',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Email Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Email
                            </label>
                            <input
                                type="email"
                                value={formData.email}
                                disabled
                                onClick={handleDisabledFieldClick}
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px',
                                    cursor: 'not-allowed'
                                }}
                            />
                        </div>

                        {/* Password Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Password
                            </label>
                            <input
                                type="password"
                                value={formData.password}
                                disabled
                                onClick={handleDisabledFieldClick}
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '12px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px',
                                    cursor: 'not-allowed'
                                }}
                            />
                        </div>

                        {/* Job Title Field */}
                        <div style={{ marginBottom: '24px' }}>
                            <label
                                style={{
                                    display: 'block',
                                    fontFamily: 'Manrope',
                                    fontWeight: 500,
                                    fontSize: '16px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.154px',
                                    color: '#015FA6',
                                    marginBottom: '14px'
                                }}
                            >
                                Job Title
                            </label>
                            <input
                                type="text"
                                value={formData.jobTitle}
                                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                                placeholder="Talent Acquisition Lead"
                                style={{
                                    width: '100%',
                                    height: '55px',
                                    padding: '10px 12px',
                                    fontFamily: 'Manrope',
                                    fontSize: '16px',
                                    color: '#015FA6',
                                    background: '#F7FAFC',
                                    border: '1px solid #CBD5E0',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    letterSpacing: '-0.154px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#015FA6';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#CBD5E0';
                                }}
                            />
                        </div>

                        {/* Terms Checkbox */}
                        <div style={{ marginBottom: '40px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    defaultChecked
                                    disabled
                                    style={{
                                        accentColor: '#015FA6',
                                        width: '16px',
                                        height: '16px'
                                    }}
                                />
                                <span
                                    style={{
                                        fontFamily: 'SF Pro',
                                        fontSize: '16px',
                                        color: '#015FA6'
                                    }}
                                >
                                    I agree to the Terms of Use and Privacy Policy
                                </span>
                            </label>
                        </div>

                        {/* Warning Message */}
                        {warningMessage && (
                            <div
                                style={{
                                    background: '#FFF4E5',
                                    border: '1px solid #FFD666',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '24px'
                                }}
                            >
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#D97706',
                                        margin: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    {warningMessage}
                                </p>
                            </div>
                        )}

                        {/* Error Message */}
                        {error && (
                            <div
                                style={{
                                    background: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '24px'
                                }}
                            >
                                <p
                                    style={{
                                        fontFamily: 'Manrope',
                                        fontSize: '14px',
                                        color: '#DC2626',
                                        margin: 0
                                    }}
                                >
                                    {error}
                                </p>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            style={{
                                width: '100%',
                                height: '55px',
                                fontFamily: 'Manrope',
                                fontWeight: 600,
                                fontSize: '20px',
                                lineHeight: '28px',
                                color: '#F7FAFC',
                                background: isLoading ? '#CBD5E0' : '#015FA6',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#014580';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isLoading) {
                                    e.currentTarget.style.background = '#015FA6';
                                }
                            }}
                        >
                            {isLoading ? 'Creating Profile...' : 'Create Profile'}
                        </button>
                    </form>
                </div>
            </div>

            {/* Right Side - Hero */}
            <div className="flex-1">
                <AuthHero variant="company" />
            </div>
        </div>
    );
};

export default CompanyAdminRecruiterSetup;
